import {ChargePointCoinTypeType, PreferredPointType} from '@web/core/constants'
import {PaymoneyMemberTypeType} from '@web/slices/chargePoint/slice'

type ResultType = 'SUCCESS' | 'FAILED'
type ResultTypeByFailedRetry = 'SUCCESS' | 'FAILED' | 'FAILED_RETRY_AVAILABLE'
type RechargeLimitType = 'BALANCE' | 'MONTH' | 'ONCE' | 'MINOR'

export type SubDetail = {
    /** 포인트 하위구분 이름 */
    text: string
    /** 포인트 하위구분의 잔액 */
    balance: number
}

export type RewardPointDescription = {
    /** 포인트 명 */
    text: string
    /** 포인트 잔액 */
    balance: number
    /** 로고 이미지의 URL */
    logoImageUrl?: string
    /** 포인트의 상세정보 */
    rewardPointDescriptionDetails?: SubDetail[]
}

/** 주문서 적립포인트 */
export type RewardPoint = {
    /** 적립포인트 조회 성공 여부 */
    success: boolean
    /** 적립포인트 잔액 */
    rewardPointBalance: Maybe<number>
    /** 현금성포인트 잔액 */
    cashableRewardPointBalance: Maybe<number>
    /**
     * 적립포인트 상세
     * voucher 값이 있는 경우에만 내려오는 값
     **/
    rewardPointDescriptions: RewardPointDescription[]
}

/** 충전포인트 잔액 */
export type ChargePointBalance = {
    /** 충전포인트 잔액 조회 성공 여부 타입 */
    resultType: Maybe<ResultTypeByFailedRetry>
    /** 충전포인트 잔액 */
    balance: Maybe<number>
    /** 충전포인트 사용가능 잔액 */
    availableBalance: Maybe<number>
    /** 충전포인트 회원 재조회 필요 여부 */
    shouldReloadChargePointUser: boolean
}

/** 충전 가능 충전포인트 */
export type RechargeableChargePoint = {
    /** 충전포인트 잔액 조회 성공 여부 타입 */
    resultType: Maybe<ResultTypeByFailedRetry>
    /** 충전포인트 충전 가능 금액 */
    reloadableRechargePointAmount: Maybe<number>
    /** 충전포인트 충전 제한 요인 */
    rechargeLimitType: Maybe<RechargeLimitType>
}

/** 주문서 충전포인트 */
type ChargePoint = {
    /** 충전포인트 회원 */
    chargePointUser: {
        /** 충전포인트 회원 조회 성공 여부 */
        resultType: Maybe<ResultType>
        /** 충전포인트 회원 상태 */
        userStatus: Maybe<PayMoneyUserStatusType>
        /**
         * 충전포인트 코인 타입
         * @deprecated 하위 호환성을 위해 유지되지만 coinType 대신 memberType을 사용하세요.
         */
        coinType: Maybe<ChargePointCoinTypeType>
        /** 마스킹된 페이머니 통장 계좌번호 */
        maskedPaymoneyBankAccountNo: Maybe<string>
        /** 충전 제한 상태 */
        rechargePointReloadingLimited: boolean
        /** 페이머니 제휴통장 타입 */
        memberType: Maybe<PaymoneyMemberTypeType>
    }
    /** 충전포인트 잔액 */
    chargePointBalance: ChargePointBalance
    /** 충전 가능 충전포인트 */
    rechargeableChargePoint: RechargeableChargePoint
}

/** 네이버페이 포인트 */
export interface NaverPayPoint {
    /**
     * 총 보유 포인트
     * - 적립 + 충전 (1.0, 3.0 유저) or 적립포인트 (2.0, 4.0, 4.1, 4.2 유저)
     **/
    naverpayPointBalance: number
    /** 주문서 적립포인트 */
    rewardPoint: RewardPoint
    /** 주문서 충전포인트 */
    chargePoint: ChargePoint
    /** 포인트 전액 사용 여부 */
    usePointAll: boolean
    /**
     * 최근결제 정보에 따른 네이버통장 혜택 여부
     * - 최근 충전 수단이 네이버통장이면 true
     **/
    naverBankbookBenefitOnRecentPayment: boolean
    /** AML 안내 문구 노출 여부 */
    exposeAmlGuideMessage: boolean
    /** 구매자 포인트/페이머니 선호 소진 순서 */
    preferredPointExhaustionList: PreferredPointType[]
    /**
     * 구매자 포인트/페이머니 선호 소진 순서 설정 가능 여부
     * 최근결제수단 조회 (프로파일) API 호출 실패 시 false
     **/
    pointExhaustionListAvailable: boolean
}

export const getNaverPayPointByApi = (): NaverPayPoint => {
    return {
        naverpayPointBalance: 0,
        rewardPoint: {
            success: false,
            rewardPointBalance: null,
            cashableRewardPointBalance: null,
            rewardPointDescriptions: [],
        },
        chargePoint: {
            chargePointUser: {
                resultType: null,
                userStatus: null,
                coinType: null,
                maskedPaymoneyBankAccountNo: null,
                rechargePointReloadingLimited: false,
                memberType: '000',
            },
            chargePointBalance: {
                resultType: null,
                balance: 0,
                availableBalance: 0,
                shouldReloadChargePointUser: false,
            },
            rechargeableChargePoint: {
                resultType: null,
                reloadableRechargePointAmount: null,
                rechargeLimitType: null,
            },
        },
        usePointAll: false,
        naverBankbookBenefitOnRecentPayment: false,
        exposeAmlGuideMessage: false,
        preferredPointExhaustionList: [],
        pointExhaustionListAvailable: false,
    }
}
